import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CheckCircleIcon,
    UserIcon,
    CalendarIcon,
    CreditCardIcon,
    ArrowRightIcon,
    SparklesIcon,
    HomeIcon,
    BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import Helper from '../helpers';

const SignupComplete = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userName, setUserName] = useState('');
    
    // Get state from navigation (from PaymentComplete)
    const { venueKey, venueName, fromPayment, mode } = location.state || {};
    
    // Determine if this is a venue admin signup
    const isVenueAdmin = mode === 'venue-admin';

    useEffect(() => {
        const initialize = async () => {
            try {
                // Check if user is logged in
                const token = await Helper.getCookie('wh-jwt');
                const userId = await Helper.getUserId();
                
                if (userId && token) {
                    // User is logged in
                    setIsLoggedIn(true);
                    
                    // Try to get user name from Helper or JWT
                    const name = await Helper.getUserName?.() || '';
                    setUserName(name);
                } else {
                    // New user - not logged in yet
                    setIsLoggedIn(false);
                }
            } catch (error) {
                console.error('Error initializing signup complete page:', error);
                setIsLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, []);

    const handleContinueToDashboard = () => {
        if (isVenueAdmin) {
            // Venue admin goes to venue dashboard
            navigate('/venue-dashboard');
        } else if (venueKey) {
            // Regular user goes to venue-specific dashboard
            navigate(`/dashboard`);
        } else {
            navigate('/dashboard');
        }
    };

    const handleViewSchedule = () => {
        if (venueKey) {
            navigate(`/sessions`);
        } else {
            navigate('/schedule');
        }
    };

    const handleLoginAndContinue = () => {
        // Redirect to login with return URL
        navigate('/login', { 
            state: { 
                returnTo: '/dashboard',
                message: 'Please log in to access your new subscription' 
            } 
        });
    };

    const handleGoHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-luxury-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-luxury-black py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Success Animation */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-600/20 rounded-full mb-6 animate-bounce-slow">
                        <CheckCircleIcon className="h-12 w-12 text-emerald-400" />
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gradient-emerald mb-4">
                        {isVenueAdmin ? 'Welcome to WorldsHighStreet!' : (isLoggedIn ? 'Subscription Active!' : 'Account Created!')}
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-luxury-pearl/70 font-body mb-2">
                        {isVenueAdmin 
                            ? 'Your venue has been set up successfully'
                            : isLoggedIn 
                                ? `Welcome back${userName ? `, ${userName}` : ''}! Your subscription is now active`
                                : 'Your account has been created and your subscription is ready'
                        }
                    </p>

                    {!isVenueAdmin && venueName && (
                        <p className="text-md text-luxury-pearl/60 font-body flex items-center justify-center gap-2">
                            <BuildingStorefrontIcon className="h-5 w-5" />
                            <span>at {venueName}</span>
                        </p>
                    )}
                </div>

                {/* Main Content Card */}
                <div className="card-luxury p-6 sm:p-8 mb-6">
                    {/* Venue Admin Success */}
                    {isVenueAdmin && (
                        <div className="mb-8 pb-8 border-b border-emerald-600/20">
                            <h2 className="text-xl font-display font-semibold text-emerald-300 mb-4 flex items-center">
                                <BuildingStorefrontIcon className="h-6 w-6 mr-2" />
                                Venue Setup Complete
                            </h2>
                            <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-xl p-4">
                                <p className="text-luxury-pearl/80 mb-2">
                                    Your venue is now live on WorldsHighStreet! You can start adding sessions, managing users, and growing your business.
                                </p>
                                {venueName && (
                                    <p className="text-emerald-300 font-semibold">
                                        Venue: {venueName}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* What's Next Section */}
                    <div className="mb-8">
                        <h2 className="text-xl font-display font-semibold text-emerald-300 mb-6 flex items-center">
                            <SparklesIcon className="h-6 w-6 mr-2" />
                            What's Next?
                        </h2>
                        
                        <div className="space-y-4">
                            {/* Step 1 */}
                            <div className="flex items-start gap-4 p-4 bg-luxury-graphite/30 rounded-xl border border-emerald-600/20">
                                <div className="flex-shrink-0 w-8 h-8 bg-emerald-600/20 rounded-full flex items-center justify-center">
                                    <span className="text-emerald-300 font-bold">1</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-display font-semibold text-luxury-pearl mb-1">
                                        Check Your Email
                                    </h3>
                                    <p className="text-sm text-luxury-pearl/60">
                                        {isVenueAdmin
                                            ? "We've sent you important information about setting up your venue and managing your subscription."
                                            : isLoggedIn
                                                ? "You'll receive a confirmation email with your updated subscription details."
                                                : "We've sent you a confirmation email with your login credentials and next steps."
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="flex items-start gap-4 p-4 bg-luxury-graphite/30 rounded-xl border border-emerald-600/20">
                                <div className="flex-shrink-0 w-8 h-8 bg-emerald-600/20 rounded-full flex items-center justify-center">
                                    <span className="text-emerald-300 font-bold">2</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-display font-semibold text-luxury-pearl mb-1">
                                        {isVenueAdmin ? 'Set Up Your Venue' : (isLoggedIn ? 'Explore Your Dashboard' : 'Sign In to Your Account')}
                                    </h3>
                                    <p className="text-sm text-luxury-pearl/60">
                                        {isVenueAdmin
                                            ? "Add your venue details, create sessions, and customize your offerings for members."
                                            : isLoggedIn
                                                ? "Access your personalized dashboard to view classes, track progress, and manage your account."
                                                : "Use your email and password to sign in and start using your subscription."
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Step 3 */}
                            <div className="flex items-start gap-4 p-4 bg-luxury-graphite/30 rounded-xl border border-emerald-600/20">
                                <div className="flex-shrink-0 w-8 h-8 bg-emerald-600/20 rounded-full flex items-center justify-center">
                                    <span className="text-emerald-300 font-bold">3</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-display font-semibold text-luxury-pearl mb-1">
                                        {isVenueAdmin ? 'Invite Your First Members' : 'Book Your First Session'}
                                    </h3>
                                    <p className="text-sm text-luxury-pearl/60">
                                        {isVenueAdmin
                                            ? "Share your venue link with potential members and start growing your community."
                                            : "Browse available sessions and book your spot. Your subscription tokens are ready to use!"
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Details */}
                    {fromPayment && (
                        <div className="mb-8 pb-8 border-b border-emerald-600/20">
                            <h2 className="text-xl font-display font-semibold text-emerald-300 mb-4 flex items-center">
                                <CreditCardIcon className="h-6 w-6 mr-2" />
                                {isVenueAdmin ? 'Platform Subscription Active' : 'Subscription Active'}
                            </h2>
                            <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                                    <span className="font-semibold text-emerald-300">Payment Processed Successfully</span>
                                </div>
                                <p className="text-sm text-luxury-pearl/70 ml-7">
                                    {isVenueAdmin
                                        ? "Your platform subscription is active. You can manage your subscription and billing from your venue dashboard."
                                        : "Your subscription is now active and will renew automatically. You can manage your subscription from your dashboard."
                                    }
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-3">
                        {isLoggedIn ? (
                            <>
                                <button
                                    onClick={handleContinueToDashboard}
                                    className="w-full btn-primary flex items-center justify-center gap-2 group"
                                >
                                    <span>{isVenueAdmin ? 'Go to Venue Dashboard' : 'Go to Dashboard'}</span>
                                    <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </button>

                                {!isVenueAdmin && (
                                    <button
                                        onClick={handleViewSchedule}
                                        className="w-full btn-outline flex items-center justify-center gap-2 group"
                                    >
                                        <CalendarIcon className="h-5 w-5" />
                                        <span>View Schedule</span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleLoginAndContinue}
                                    className="w-full btn-primary flex items-center justify-center gap-2 group"
                                >
                                    <UserIcon className="h-5 w-5" />
                                    <span>Sign In to Your Account</span>
                                    <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </>
                        )}

                        <button
                            onClick={handleGoHome}
                            className="w-full text-emerald-300/70 hover:text-emerald-300 transition-colors py-3 flex items-center justify-center gap-2"
                        >
                            <HomeIcon className="h-5 w-5" />
                            <span className="text-sm">Return to Home</span>
                        </button>
                    </div>
                </div>

                {/* Additional Help Section */}
                <div className="card-luxury p-6 bg-luxury-graphite/30">
                    <h3 className="font-display font-semibold text-luxury-pearl mb-3">
                        Need Help?
                    </h3>
                    <p className="text-sm text-luxury-pearl/60 mb-4">
                        If you have any questions or need assistance, our support team is here to help.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {!isVenueAdmin && venueKey && venueName && (
                            <a
                                href={`/venue/${venueKey}/contact`}
                                className="text-emerald-300 hover:text-emerald-400 text-sm font-medium transition-colors"
                            >
                                Contact {venueName} →
                            </a>
                        )}
                        <a
                            href="/help"
                            className="text-emerald-300 hover:text-emerald-400 text-sm font-medium transition-colors"
                        >
                            View Help Center →
                        </a>
                        <a
                            href="mailto:support@worldshighstreet.com"
                            className="text-emerald-300 hover:text-emerald-400 text-sm font-medium transition-colors"
                        >
                            Email Support →
                        </a>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes bounce-slow {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

export default SignupComplete;
